<script setup>
import { computed, ref, watch } from 'vue'
import Pagination from './Pagination.vue'

const props = defineProps({
	items: {
		type: Array,
		default: () => [],
	},
	pageSize: {
		type: Number,
		default: 5,
	},
	pageSizeOptions: {
		type: Array,
		default: () => [5, 10, 20],
	},
	showPageSizeSelector: {
		type: Boolean,
		default: false,
	},
	listTag: {
		type: String,
		default: 'ul',
	},
	listClass: {
		type: String,
		default: '',
	},
	itemKey: {
		type: String,
		default: '_id',
	},
	hidePagination: {
		type: Boolean,
		default: false,
	},
	hidePaginationWhenSinglePage: {
		type: Boolean,
		default: true,
	},
	source: {
		type: String,
		default: '',
	},
})

const currentPage = ref(0)
const internalPageSize = ref(props.pageSize)

const effectivePageSize = computed(() => internalPageSize.value)

const totalPages = computed(() => {
	if (props.items.length === 0) return 1
	return Math.ceil(props.items.length / effectivePageSize.value)
})

const paginatedItems = computed(() => {
	if (props.source !== 'playlist' && props.source !== 'dynamic') return props.items
	const start = currentPage.value * effectivePageSize.value
	return props.items.slice(start, start + effectivePageSize.value)
})

const showPagination = computed(() => {
	if (props.source !== 'playlist' && props.source !== 'dynamic') return false
	if (props.hidePagination) return false
	if (!props.hidePaginationWhenSinglePage) return props.items.length > 0
	return totalPages.value > 1
})

const showPageSizeControl = computed(
	() =>
		(props.source === 'playlist' || props.source === 'dynamic') &&
		props.showPageSizeSelector &&
		props.pageSizeOptions.length > 1
)

const getItemKey = (item, index) => item?.[props.itemKey] ?? index

const goToPage = (page) => {
	currentPage.value = page
}

const onPageSizeChange = (event) => {
	internalPageSize.value = Number(event.target.value)
	currentPage.value = 0
}

watch(
	() => props.pageSize,
	(size) => {
		internalPageSize.value = size
		currentPage.value = 0
	}
)

watch(
	() => props.items.length,
	() => {
		currentPage.value = 0
	}
)

watch(totalPages, (pages) => {
	if (currentPage.value >= pages) {
		currentPage.value = Math.max(0, pages - 1)
	}
})

</script>

<template>
	<div class="paginated-list">
		<component :is="listTag" :class="listClass">
			<template v-for="(item, index) in paginatedItems" :key="getItemKey(item, index)">
				<slot :item="item" :index="index" />
			</template>
		</component>
		<div v-if="showPagination || showPageSizeControl" class="flex flex-col items-center gap-2 mt-2">
			<label v-if="showPageSizeControl" class="flex items-center gap-2 text-tiny text-surface-7">
				<span>Per page</span>
				<select
					:value="internalPageSize"
					class="bg-surface-3 border border-surface-4 rounded px-2 py-1 text-surface-text-1 text-tiny"
					@change="onPageSizeChange"
				>
					<option v-for="option in pageSizeOptions" :key="option" :value="option">
						{{ option }}
					</option>
				</select>
			</label>
			<Pagination
				v-if="showPagination"
				:current-page="currentPage"
				:pages="totalPages"
				variant="light"
				@go-to-page="goToPage"
				class="mr-auto"
			/>
		</div>
	</div>
</template>
